import { Component, OnInit } from '@angular/core';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { InventoryUserTableModel } from 'src/app/models/tables/inventory/inventory-user-table.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { InventoryUserService } from 'src/app/services/inventory/inventory-user.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-inventory-user',
  templateUrl: './inventory-user.component.html',
  styleUrls: ['./inventory-user.component.scss'],
  standalone: false
})
export class InventoryUserComponent implements OnInit {

  ascendant: boolean = true;
  companyId: number = 0;
  columnSort: string | null = null;
  data: PagedResponseModel<InventoryUserTableModel> =
    new PagedResponseModel<InventoryUserTableModel>();
  disabledButton: boolean = false;
  filters: ColumnResponseModel[] = [];
  header: ColumnResponseModel[] = [];
  inventoryId: number = 0;
  inventoryName: string = '';
  suscription: Subscription;
  limit: number = 10;
  output$: Subject<any>;
  page: number = 1;
  tableActions: TableAction[] = [
    {
      label: 'Modificar',
      icon: 'pi pi-sync',
      action: 'edit'
    },
  ];
  userId: number = 0;

  constructor(public config: DynamicDialogConfig,
    private confirmDialogService: ConfirmDialogService,
    private toastHandlerService: ToastHandlerService,
    private inventoryUserService: InventoryUserService) { }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.suscription = input$?.subscribe((inputs: any) => {
      this.inventoryId = inputs.inventoryId;
      this.inventoryName = inputs.inventoryName;
    });
    await Promise.all([
      this.getColumnsInventoryUser(),
      this.getAllInventoryUsersWithPagination()
    ]);
  }

  async getAllInventoryUsersWithPagination() {
    try {
      this.data = await firstValueFrom(
        this.inventoryUserService.getAllInventoryUsersWithPagination(
          this.inventoryId, this.page, this.limit, this.columnSort, this.ascendant, this.filters
        )
      );
    } catch (error) {
      console.error('Error al obtener los permisos de inventario de los usuarios.', error);
    }
  }

  async getColumnsInventoryUser() {
    try {
      this.header = await firstValueFrom(this.inventoryUserService.getColumnsInventoryUser());
    } catch (error) {
      console.error('Error al obtener las columnas de permisos de inventario por usuario.', error);
    }
  }

  async onAction(event: { action: string, item?: InventoryUserTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      if (await this.confirmDialogService.confirm(
        '¿Realmente desea modificar los permisos de inventario para este usuario?'
      )) {
        this.userId = event.item.userId;
        await this.savePermissions();
      }
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    await this.getAllInventoryUsersWithPagination();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  async saveInventoryUserPermission(inventoryId: number, userId: number) {
    try {
      await firstValueFrom(this.inventoryUserService.saveInventoryUser(inventoryId, userId));
    } catch (error) {
      console.error('Error al guardar el permiso de inventario para el usuario.', error);
    }
  }

  async savePermissions() {
    try {
      await this.saveInventoryUserPermission(this.inventoryId, this.userId);
      await this.getAllInventoryUsersWithPagination();
      this.toastHandlerService.showToast(
        'success',
        'Los permisos de inventario del usuario se actualizaron correctamente.'
      );
    } catch (error) {
      console.error('Error al guardar el permiso de inventario para el usuario.', error);
    }
  }
}
